import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Upload, Loader2, ShieldCheck, Mail } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const inputCls =
  "w-full border border-black/15 bg-white px-4 py-3 text-[#2b2520] outline-none transition-colors focus:border-[#c99a63] placeholder:text-neutral-400";

export default function Auth() {
  const [params] = useSearchParams();
  const redirect = params.get("redirect") || "/";
  const navigate = useNavigate();
  const { login } = useAuth();

  const [mode, setMode] = useState("login");
  const [step, setStep] = useState("form"); // form | verify
  const [busy, setBusy] = useState(false);
  const [devCode, setDevCode] = useState("");

  const [reg, setReg] = useState({ name: "", email: "", phone: "", password: "", dob: "" });
  const [idImage, setIdImage] = useState("");
  const [idPreview, setIdPreview] = useState("");
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [code, setCode] = useState("");
  const [pendingEmail, setPendingEmail] = useState("");

  const onIdFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setIdImage(reader.result);
      setIdPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const doRegister = async (e) => {
    e.preventDefault();
    if (!reg.name || !reg.email || !reg.phone || !reg.password || !reg.dob) {
      toast.error("Please fill in all fields");
      return;
    }
    if (reg.phone.replace(/\D/g, "").length < 10) {
      toast.error("Please enter a valid phone number");
      return;
    }
    if (!idImage) {
      toast.error("Please upload a photo of your government ID");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register", {
        ...reg,
        id_image_base64: idImage,
      });
      setPendingEmail(reg.email);
      if (data.dev_code) setDevCode(data.dev_code);
      if (data.id_verification === "approved") {
        toast.success("ID verified — you're 21+. Now verify your email.");
      } else if (data.id_verification === "rejected") {
        toast.error("ID could not be verified as 21+. An admin will review it.");
      } else {
        toast.message("ID uploaded — pending review. Verify your email to continue.");
      }
      setStep("verify");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", loginForm);
      login(data.token, data.user);
      toast.success("Welcome back");
      const isStaff = ["admin", "owner", "manager", "employee"].includes(data.user?.role);
      navigate(isStaff && redirect === "/" ? "/admin" : redirect);
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail);
      if (err.response?.status === 403 && detail.toLowerCase().includes("verify")) {
        setPendingEmail(loginForm.email);
        setMode("signup");
        setStep("verify");
        const { data } = await api.post("/auth/resend-code", { email: loginForm.email });
        if (data.dev_code) setDevCode(data.dev_code);
        toast.message("Please verify your email. A new code was issued.");
      } else {
        toast.error(detail);
      }
    } finally {
      setBusy(false);
    }
  };

  const doVerify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-email", {
        email: pendingEmail,
        code,
      });
      login(data.token, data.user);
      toast.success("Email verified. Welcome to The Good Life.");
      navigate(redirect);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    try {
      const { data } = await api.post("/auth/resend-code", { email: pendingEmail });
      if (data.dev_code) setDevCode(data.dev_code);
      toast.success("A new code was issued");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="mx-auto max-w-md px-6 py-16">
      {step === "verify" ? (
        <div data-testid="verify-step">
          <div className="mb-8 text-center">
            <Mail className="mx-auto mb-4 h-10 w-10 text-[#a06d34]" strokeWidth={1.2} />
            <h1 className="mb-2 text-3xl font-black text-[#2b2520]">Verify your email</h1>
            <p className="text-sm text-neutral-600">
              We sent a 6-digit code to{" "}
              <span className="text-[#2b2520]">{pendingEmail}</span>.
            </p>
          </div>
          {devCode && (
            <div
              data-testid="dev-code-banner"
              className="mb-6 border border-[#c99a63]/50 bg-[#c99a63]/15 p-4 text-center text-sm text-[#a06d34]"
            >
              Email sending is off. Your code is{" "}
              <span className="font-bold tracking-widest">{devCode}</span>
            </div>
          )}
          <form onSubmit={doVerify} className="space-y-4">
            <input
              data-testid="code-input"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Enter 6-digit code"
              maxLength={6}
              className={`${inputCls} text-center text-2xl tracking-[0.5em]`}
            />
            <button
              data-testid="verify-submit"
              disabled={busy}
              className="flex w-full items-center justify-center gap-2 bg-[#c99a63] py-3 text-sm font-semibold uppercase tracking-widest text-[#2b2520] transition-colors hover:bg-[#bb8850] disabled:opacity-60"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & continue"}
            </button>
          </form>
          <button
            data-testid="resend-code"
            onClick={resend}
            className="mt-4 w-full text-center text-xs uppercase tracking-widest text-neutral-500 hover:text-[#a06d34]"
          >
            Resend code
          </button>
        </div>
      ) : (
        <>
          <div className="mb-8 flex border border-black/10">
            <button
              data-testid="tab-login"
              onClick={() => setMode("login")}
              className={`flex-1 py-3 text-xs font-semibold uppercase tracking-widest transition-colors ${
                mode === "login" ? "bg-[#c99a63] text-[#2b2520]" : "text-neutral-500"
              }`}
            >
              Sign In
            </button>
            <button
              data-testid="tab-signup"
              onClick={() => setMode("signup")}
              className={`flex-1 py-3 text-xs font-semibold uppercase tracking-widest transition-colors ${
                mode === "signup" ? "bg-[#c99a63] text-[#2b2520]" : "text-neutral-500"
              }`}
            >
              Create Account
            </button>
          </div>

          {mode === "login" ? (
            <form onSubmit={doLogin} className="space-y-4" data-testid="login-form">
              <h1 className="mb-2 text-3xl font-black text-[#2b2520]">Welcome back</h1>
              <input
                data-testid="login-email"
                type="email"
                placeholder="Email"
                value={loginForm.email}
                onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                className={inputCls}
              />
              <input
                data-testid="login-password"
                type="password"
                placeholder="Password"
                value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                className={inputCls}
              />
              <button
                data-testid="login-submit"
                disabled={busy}
                className="flex w-full items-center justify-center gap-2 bg-[#c99a63] py-3 text-sm font-semibold uppercase tracking-widest text-[#2b2520] transition-colors hover:bg-[#bb8850] disabled:opacity-60"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
              </button>
            </form>
          ) : (
            <form onSubmit={doRegister} className="space-y-4" data-testid="signup-form">
              <h1 className="mb-2 text-3xl font-black text-[#2b2520]">Create your account</h1>
              <p className="mb-2 text-sm text-neutral-600">
                We verify every buyer is 21+ with a government ID.
              </p>
              <input
                data-testid="signup-name"
                placeholder="Full name"
                value={reg.name}
                onChange={(e) => setReg({ ...reg, name: e.target.value })}
                className={inputCls}
              />
              <input
                data-testid="signup-email"
                type="email"
                placeholder="Email"
                value={reg.email}
                onChange={(e) => setReg({ ...reg, email: e.target.value })}
                className={inputCls}
              />
              <input
                data-testid="signup-phone"
                type="tel"
                required
                placeholder="Phone number"
                value={reg.phone}
                onChange={(e) => setReg({ ...reg, phone: e.target.value })}
                className={inputCls}
              />
              <p className="-mt-2 text-[11px] text-neutral-500">
                We use your phone to reach you if you win our monthly Free Ounce giveaway.
              </p>
              <input
                data-testid="signup-password"
                type="password"
                placeholder="Password"
                value={reg.password}
                onChange={(e) => setReg({ ...reg, password: e.target.value })}
                className={inputCls}
              />
              <div>
                <label className="mb-1 block text-xs uppercase tracking-widest text-neutral-500">
                  Date of birth
                </label>
                <input
                  data-testid="signup-dob"
                  type="date"
                  value={reg.dob}
                  onChange={(e) => setReg({ ...reg, dob: e.target.value })}
                  className={`${inputCls} [color-scheme:light]`}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs uppercase tracking-widest text-neutral-500">
                  Government ID photo
                </label>
                <label
                  data-testid="id-upload-label"
                  className="flex cursor-pointer flex-col items-center justify-center gap-2 border border-dashed border-black/20 bg-white py-8 text-neutral-500 transition-colors hover:border-[#c99a63]"
                >
                  {idPreview ? (
                    <img src={idPreview} alt="ID preview" className="max-h-40 object-contain" />
                  ) : (
                    <>
                      <Upload className="h-6 w-6" strokeWidth={1.2} />
                      <span className="text-xs uppercase tracking-widest">
                        Upload driver's license or ID
                      </span>
                    </>
                  )}
                  <input
                    data-testid="id-file-input"
                    type="file"
                    accept="image/*"
                    onChange={onIdFile}
                    className="hidden"
                  />
                </label>
              </div>
              <div className="flex items-start gap-2 text-xs text-neutral-500">
                <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#a06d34]" strokeWidth={1.5} />
                Your ID is scanned by AI to confirm you're 21+. It's stored securely
                and only reviewed by staff for compliance.
              </div>
              <button
                data-testid="signup-submit"
                disabled={busy}
                className="flex w-full items-center justify-center gap-2 bg-[#c99a63] py-3 text-sm font-semibold uppercase tracking-widest text-[#2b2520] transition-colors hover:bg-[#bb8850] disabled:opacity-60"
              >
                {busy ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Verifying ID…
                  </>
                ) : (
                  "Create account"
                )}
              </button>
            </form>
          )}
        </>
      )}
    </div>
  );
}
